(() => {
  if (window.__FAJ_INDUSTRY_TESTIMONIALS_V1__) return;
  window.__FAJ_INDUSTRY_TESTIMONIALS_V1__ = true;

  const instances = new WeakMap();

  class FAJIndustryTestimonials {
    constructor(section) {
      this.section = section;
      this.carousel = section.querySelector('[data-fajit-carousel]');
      this.slides = Array.from(section.querySelectorAll('[data-fajit-slide]'));
      this.previousButton = section.querySelector('[data-fajit-previous]');
      this.nextButton = section.querySelector('[data-fajit-next]');
      this.currentCounter = section.querySelector('[data-fajit-current]');
      this.totalCounter = section.querySelector('[data-fajit-total]');
      this.dotsContainer = section.querySelector('[data-fajit-dots]');

      this.wrapAround = section.dataset.wrapAround === 'true';
      this.autoPlay = section.dataset.autoPlay === 'true';
      this.autoPlaySpeed = Number(section.dataset.autoPlaySpeed || 5000);

      this.flickity = null;
      this.waitTimer = null;
      this.waitAttempts = 0;
      this.currentIndex = 0;

      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    init() {
      if (!this.carousel || !this.slides.length) return;

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.carousel.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.updateTotal();
      this.waitForFlickity();
    }

    waitForFlickity() {
      if (typeof window.Flickity !== 'undefined') {
        this.initFlickity();
        return;
      }

      this.waitAttempts += 1;

      if (this.waitAttempts > 60) {
        console.warn('FAJ testimonials: Flickity was not available.');
        this.buildStaticDots();
        return;
      }

      this.waitTimer = window.setTimeout(() => this.waitForFlickity(), 100);
    }

    initFlickity() {
      if (this.flickity || typeof window.Flickity === 'undefined') return;

      this.flickity = new window.Flickity(this.carousel, {
        cellAlign: 'left',
        contain: true,
        draggable: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: this.wrapAround,
        groupCells: true,
        adaptiveHeight: false,
        setGallerySize: true,
        percentPosition: true,
        selectedAttraction: 0.025,
        friction: 0.28,
        autoPlay: this.autoPlay ? this.autoPlaySpeed : false,
        pauseAutoPlayOnHover: true,
        accessibility: true
      });

      this.flickity.on('ready', () => {
        this.currentIndex = this.flickity.selectedIndex || 0;
        this.buildDots();
        this.updateState();
      });

      this.flickity.on('change', (index) => {
        this.currentIndex = index;
        this.updateState();
      });

      this.flickity.on('settle', () => this.updateState());

      window.requestAnimationFrame(() => {
        this.flickity?.resize();
        this.buildDots();
        this.updateState();
      });
    }

    getPageCount() {
      return this.flickity?.slides?.length || this.slides.length;
    }

    updateTotal() {
      if (this.totalCounter) {
        this.totalCounter.textContent = String(this.slides.length).padStart(2, '0');
      }
    }

    updateState() {
      const pageCount = this.getPageCount();

      if (this.currentCounter) {
        this.currentCounter.textContent = String(this.currentIndex + 1).padStart(2, '0');
      }

      if (!this.wrapAround) {
        if (this.previousButton) {
          this.previousButton.disabled = this.currentIndex <= 0;
        }

        if (this.nextButton) {
          this.nextButton.disabled = this.currentIndex >= pageCount - 1;
        }
      }

      this.updateDots();
    }

    buildDots() {
      if (!this.dotsContainer) return;

      const pageCount = this.getPageCount();
      this.dotsContainer.innerHTML = '';

      for (let index = 0; index < pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fajit__dot';
        dot.setAttribute('aria-label', `Go to testimonial page ${index + 1}`);
        dot.addEventListener('click', () => this.flickity?.select(index));
        this.dotsContainer.appendChild(dot);
      }

      this.updateDots();
    }

    buildStaticDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '<span class="fajit__dot is-selected"></span>';
    }

    updateDots() {
      const dots = Array.from(
        this.dotsContainer?.querySelectorAll('.fajit__dot') || []
      );

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-selected', index === this.currentIndex);
      });
    }

    onPrevious() {
      this.flickity?.previous(this.wrapAround);
    }

    onNext() {
      this.flickity?.next(this.wrapAround);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.onPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.onNext();
      }
    }

    onResize() {
      window.requestAnimationFrame(() => {
        this.flickity?.resize();
        this.buildDots();
      });
    }

    destroy() {
      if (this.waitTimer) window.clearTimeout(this.waitTimer);

      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.carousel?.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);

      this.flickity?.destroy();
      this.flickity = null;
    }
  }

  const initSection = (section) => {
    if (!section || instances.has(section)) return;
    const instance = new FAJIndustryTestimonials(section);
    instances.set(section, instance);
    instance.init();
  };

  const destroySection = (section) => {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  };

  const initAll = (root = document) => {
    if (root.matches?.('[data-fajit]')) initSection(root);
    root.querySelectorAll?.('[data-fajit]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-fajit]')) {
      destroySection(event.target);
    }

    event.target.querySelectorAll?.('[data-fajit]').forEach(destroySection);
  });
})();
